import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { SimpleGrid } from '@chakra-ui/react';
import { isEqual } from 'lodash';

import { ProfessionalDataFormSkeleton } from './skeletons/ProfessionalDataFormSkeleton';

import Typography from 'src/components/Typography';
import MevoInput from 'src/components/Input';
import { Flex } from 'src/components/UI/Flex';
import { Button } from 'src/components/UI/Button';
import { Select } from 'src/components/UI/Select/Select';
import { FormControl } from 'src/components/UI/FormControl';
import { FormErrorMessage } from 'src/components/UI/FormErrorMessage';

import { Yup } from 'src/helpers/Yup';
import { counsels } from 'src/helpers/professionalCounsels';

import { usePrescriberContext } from 'src/providers/PrescriberProvider';
import { useFeedbackContext } from 'src/providers/FeedbackProvider';

import { PrescriberService } from 'src/services/prescriber';
import SelectState from 'src/components/SelectState';
import SelectRQE from 'src/components/SelectRQE';
import { Box } from 'src/components/UI/Box';
import { trackProfileProfessionalDataSaveClick } from 'src/services/tracking/events';

interface ProfessionalDataFormFields {
  counsel: string;
  UFRegister: string;
  registerNumber: string | number;
  defaultRqeId: number | string;
}

const ProfessionalDataFormSchema = Yup.object().shape({
  counsel: Yup.string().required('Informe o conselho profissional.'),
  UFRegister: Yup.string().required('Informe uma UF do Registro válida.'),
  registerNumber: Yup.string().required('Informe um Número de Registro válido.'),
  defaultRqeId: Yup.mixed().optional(),
});

const defaultRqeText = 'Não exibir';

const initialValues: ProfessionalDataFormFields = {
  counsel: '',
  UFRegister: '',
  registerNumber: '',
  defaultRqeId: defaultRqeText,
};

interface ProfessionalDataFormProps {
  onEdit?(): void;
}

export function ProfessionalDataForm(props: ProfessionalDataFormProps) {
  const [defaultValues, setDefaultValues] = useState<ProfessionalDataFormFields>(initialValues);
  const { prescriberData, setPrescriberData, getAndSetPrescriberStatus, isUserInvalid, prescriberId } =
    usePrescriberContext();
  const { openToast } = useFeedbackContext();

  const { values, errors, isSubmitting, handleChange, setValues, handleSubmit, setFieldValue, validateField } =
    useFormik<ProfessionalDataFormFields>({
      initialValues: initialValues,
      validationSchema: ProfessionalDataFormSchema,
      validateOnChange: false,
      onSubmit: onSubmit,
    });

  async function onSubmit(data: ProfessionalDataFormFields) {
    trackProfileProfessionalDataSaveClick(prescriberId || undefined);

    try {
      if (!prescriberData) return;

      const payload = {
        ...prescriberData,
        professionalRecord: {
          ...prescriberData.professionalRecord,
          number: String(data.registerNumber),
          state: data.UFRegister,
          issuer: data.counsel,
          defaultRqeId: typeof data.defaultRqeId === 'string' ? undefined : data.defaultRqeId,
        },
      };

      await PrescriberService.updatePrescriber(prescriberData._id, payload);

      await getAndSetPrescriberStatus(prescriberData._id);

      const updatedData = await PrescriberService.getPrescriber(prescriberData._id);
      setPrescriberData(updatedData);

      openToast('Dados salvos com sucesso.', {
        type: 'success',
        duration: 2000,
      });

      if (props.onEdit) {
        props.onEdit();
      }
    } catch (error: any) {
      if (error.response.status === 400) {
        openToast('Algumas informações não correspondem aos dados encontrados no Conselho Federal de Medicina.', {
          type: 'error',
          duration: 6000,
        });

        return;
      }

      if (error.response.status === 500) {
        openToast('Serviço se encontra fora do ar. Tente novamente mais tarde.', {
          type: 'error',
          duration: 6000,
        });

        return;
      }

      openToast('Ocorreu um erro ao salvar seus dados. Tente novamente em instantes.', {
        type: 'error',
        duration: 2000,
      });
    }
  }

  useEffect(() => {
    if (!prescriberData) return;

    setValues({
      counsel: prescriberData.professionalRecord.issuer,
      UFRegister: prescriberData.professionalRecord.state,
      registerNumber: prescriberData.professionalRecord.number,
      defaultRqeId: prescriberData.professionalRecord.defaultRqeId ?? defaultRqeText,
    });

    setDefaultValues({
      counsel: prescriberData.professionalRecord.issuer,
      UFRegister: prescriberData.professionalRecord.state,
      registerNumber: prescriberData.professionalRecord.number,
      defaultRqeId: prescriberData.professionalRecord.defaultRqeId ?? defaultRqeText,
    });
  }, [prescriberData, setValues]);

  // const isSameData = isEqual(defaultValues, values);

  if (!prescriberData) {
    return <ProfessionalDataFormSkeleton />;
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        height: '100%',
      }}
      data-testId={'professional-data-form'}
    >
      <Flex
        flexDirection={'column'}
        gap={'20px'}
        bgColor={{
          base: 'transparent',
          lg: 'white',
        }}
        borderRadius={'20px'}
        p={{ base: '0px', sm: '32px' }}
        h={'full'}
      >
        <Box display={{ base: 'none', sm: 'block' }}>
          <Typography model={'h2'} size={'large'} weight={'bold'}>
            Dados profissionais
          </Typography>
        </Box>

        <FormControl isInvalid={!!errors.counsel}>
          <Typography model={'caption'} size={'normal'} weight={'bold'} textTransform={'uppercase'}>
            Conselho profissional*
          </Typography>

          <Select
            name={'counsel'}
            placeholder="Selecione o conselho profissional"
            h={'45px'}
            mt={'10px'}
            value={values.counsel}
            isDisabled={!isUserInvalid}
            data-testId={'select-counsel'}
            onChange={(e) => setFieldValue('counsel', e.target.value)}
          >
            {counsels.map((counsel) => (
              <option value={counsel.initials} key={counsel.initials}>
                {counsel.name}
              </option>
            ))}
          </Select>
          <FormErrorMessage>{errors.counsel}</FormErrorMessage>
        </FormControl>

        <SimpleGrid columns={2} gap={'20px'}>
          <FormControl isInvalid={!!errors.UFRegister}>
            <SelectState
              label={'UF do registro*'}
              placeholder={'Selecione o estado (UF)'}
              value={values.UFRegister}
              onChange={(value) => setFieldValue('UFRegister', value)}
              onBlur={() => validateField('UFRegister')}
              errorMessage={errors.UFRegister}
            />
          </FormControl>

          <FormControl isInvalid={!!errors.registerNumber}>
            <MevoInput
              name={'registerNumber'}
              placeholder={'XPTO161'}
              labelText={'N˚ do registro*'}
              onChange={handleChange}
              value={values.registerNumber}
              isDisabled={!isUserInvalid}
              m={0}
            />
            <FormErrorMessage>{errors.registerNumber}</FormErrorMessage>
          </FormControl>
        </SimpleGrid>

        <FormControl isInvalid={!!errors.defaultRqeId}>
          <SelectRQE
            label={'RQE'}
            value={values.defaultRqeId}
            onChange={(value) => {
              setFieldValue('defaultRqeId', value);
            }}
            onBlur={() => validateField('defaultRqeId')}
            rqeList={prescriberData.professionalRecord.rqeList}
            errorMessage={errors.defaultRqeId}
          />
        </FormControl>

        <Button
          type={'submit'}
          isLoading={isSubmitting}
          w={{
            base: 'full',
            md: '150px',
          }}
          h={'60px'}
          alignSelf={'flex-end'}
          mt={'auto'}
        >
          {isUserInvalid ? 'Revalidar Cadastro' : 'Salvar'}
        </Button>
      </Flex>
    </form>
  );
}
